import { AfterViewInit, Component, DestroyRef, DoCheck, ElementRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TsInputComponent } from 'tsui';

import { AddressPart } from '@shared/models';
import { GooglePlacesDirective } from '@shared/directives/google-places.directive';
import { FormFieldComponent } from '../form-field.types';
import { ResearchService } from '@modules/research/research.service';

@Component({
  templateUrl: './text-form-field.component.html',
  styleUrls: ['./../form-field.component.scss'],
})
export class TextFormFieldComponent extends FormFieldComponent implements AfterViewInit, DoCheck {
  @ViewChild(TsInputComponent) private readonly tsInput?: TsInputComponent;
  @ViewChild(GooglePlacesDirective) private readonly googlePlacesDirective?: GooglePlacesDirective;

  private _originalValue: string;

  public editing: boolean = false;
  public value: string;
  public readonly control = new FormControl('');
  // Overrides the inherited Material-specific selector: ts-input has no .mat-mdc-text-field-wrapper to target.
  public readonly commonTargetStylingClass = '.ts-input';

  // Duck-typed contract read by form-field.component.ts to avoid clobbering an in-progress edit.
  get inputControl(): ElementRef | undefined {
    return this.tsInput?.inputElement();
  }

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly resarchService: ResearchService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.value = this.model[this.fieldDef.name];
    this._originalValue = this.value;

    this.control.setValue(this.value, { emitEvent: false });
    this.updateDisabledState();

    this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.value = value;
      this.updateMask();
    });

    this.updateMask();
  }

  ngAfterViewInit(): void {
    const addressInputElement = this.tsInput?.inputElement()?.nativeElement;

    if (addressInputElement) {
      this.googlePlacesDirective?.attachTo(addressInputElement);
    }
  }

  ngDoCheck(): void {
    this.updateDisabledState();
  }

  edit(): void {
    if (!this.editing && !this.isReadonly) {
      this.editing = true;
      setTimeout(() => {
        this.inputControl?.nativeElement?.focus?.();
      }, 300);
    }
  }

  onBlur(): void {
    this.onSave();
    this.editing = false;
  }

  onSetAddress(event: AddressPart): void {
    if (event?.lat && event.lng) {
      const { lat, lng } = event;
      this.resarchService
        .reverseGeocode(null, lat, lng)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (response && response.address) {
            this.updateModel();
            this.update.emit({
              [this.fieldDef.name]: response.address,
              address: response,
            });
          }
        });
    }
  }

  updateChangedModel(model: any): void {
    this.model = model;
  }

  private onSave(): void {
    if (this._originalValue != this.value) {
      this.updateModel();
      this.update.emit({ [this.fieldDef.name]: this.value });
    }
  }

  private updateModel(): void {
    this.model[this.fieldDef.name] = this.value;
    this._originalValue = this.value;
  }

  private updateMask(): void {
    if (this.fieldMask) {
      this.mask$.next(this.fieldMask(this.value));
    }
  }

  private updateDisabledState(): void {
    if (this.isDisabled === this.control.disabled) {
      return;
    }

    if (this.isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }
}
